import React, { useCallback, useEffect, useState } from "react";
import { GENERATE_REPORT } from "../../../api/apiUrls";
import axios from "axios";
import Select from "react-select";
import {
  admStatusList,
  apsStatusOptions,
  baStatusList,
  packageCodes,
  paymentStatusOptions,
  stStatusOptions,
  visaFileStatusOptions,
  visaStatusList,
} from "../../../static/dropdown";
import VisaTable from "./VisaTable";
import { buildCondition } from "../../../static/hook";
import CustomSpinner from "../../../components/CustomSpinner";
import MenuIcon from "@mui/icons-material/Menu";
import { useSearchParams } from "react-router-dom";
import NotAuthorize from "../../../error/NotAuthorize";

const VisaDashboard = ({ setShowSideOverlay, user }) => {
  const [searchParams] = useSearchParams();
  
  const userCode = parseInt(searchParams.get("userCode"));
  const [authState, setAuthState] = useState(false);
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [visaReportStatusWise, setVisaReportStatusWise] = useState(null);
  const [visaReportStatusWiseLoading, setVisaReportStatusWiseLoading] = useState(false);
  const [visaReportWise, setVisaReportWise] = useState(null);
  const [visaReportStatusLoading, setVisaReportStatusLoading] = useState(false);
  const [baReport, setBaReport] = useState(null);
  const [baLoading, setBaLoading] = useState(false);
  const [uniWiseport, setUniWiseReport] = useState(null);
  const [uniWiseLoading, setUniWiseLoading] = useState(false);
  const [dateFilterRange, setDateFilterRange] = useState(null);
  const [filterDropdown, setFilterDropdown] = useState({
    intakeOptions: [],
    locationOptions: [],
    checklistOptions: [],
    categoryOptions: [],
    visaSopOptions: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    location: [], //1 s.locCode
    package: [], //2 s.packageCode
    intake: [], //3 s.intakeCode
    course: [], //4 s.coursetype
    ststatus: [], //5 s.ststatus
    category: [], //6 s.catCode
    paymentStatus: [], //7 s.PaymentStatus
    appIntake: [], //8 a.intakeCode
    publicPrivate: [], //9 a.publicorprivate
    visaSop: [], //10 a.visaSopstatus
    admStatus: [], //11 a.status
    baStatus: [], //12 v.BA_Status
    visaStatus: [], //13 v.VisaStatus
    visaFile: [], //14 v.VisaFileStatus
    apsStatus: [], //15 v.ApsStatus
  });

  const fetchBaseQuery = useCallback(async (query) => {
    if (!query?.trim()) {
      console.warn("No query found to execute");
      return null;
    }
    try {
      const { data } = await axios.post(GENERATE_REPORT, { query });
      return data || null;
    } catch (error) {
      console.error(
        "Error occurred in fetchBaseQuery:",
        error?.response?.data?.message || error.message || "Unknown error"
      );
      return null;
    }
  }, []);

  const fetchFilterDropdown = useCallback(async () => {
    try {
      const queries = {
        intakeOptions:
          "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
        locationOptions:
          "SELECT LocCode AS 'Code', locname AS 'Name' FROM locmas",
        checklistOptions:
          "SELECT DoctypeCode AS 'Code', DoctypeName AS 'Name' FROM DocTypeMas WHERE DoctypeCode NOT IN (11, 12, 13, 17, 26)",
        categoryOptions:
          "SELECT CatCode AS 'Code', CatName AS 'Name' FROM catmas",
        visaSopOptions:
          "SELECT compcode AS 'Code', Compname AS 'Name' FROM commontypemas WHERE compcode = 'VisaSop_Status'",
      };

      const fetchPromises = Object.entries(queries).map(
        async ([key, query]) => {
          const data = await fetchBaseQuery(query);
          return { key, data };
        }
      );

      const results = await Promise.all(fetchPromises);

      const updatedDropdowns = results.reduce((acc, { key, data }) => {
        if (data?.length) {
          acc[key] = data.map((item) => ({
            label: item.NAME || "Unknown",
            value: item.CODE || "N/A",
          }));
        } else {
          console.warn(`No data received for ${key}`);
        }
        return acc;
      }, {});

      if (Object.keys(updatedDropdowns).length) {
        setFilterDropdown((prev) => ({ ...prev, ...updatedDropdowns }));
      }
    } catch (error) {
      console.error("Error fetching filter dropdown options:", error.message);
    }
  }, [fetchBaseQuery]);

  const fetchStatusWiseVisaReports = async () => {
    setVisaReportStatusWiseLoading(true);
    try {
      const filters = [];
      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.catCode", filterOptions.category, true),
        buildCondition("s.PaymentStatus", filterOptions.paymentStatus),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("a.intakeCode", filterOptions.appIntake, true),
        buildCondition("a.status", filterOptions.admStatus),
        buildCondition("a.publicorprivate", filterOptions.publicPrivate),
        buildCondition("v.VisaStatus", filterOptions.visaStatus),
        buildCondition("v.BA_Status", filterOptions.baStatus),
        buildCondition("v.visaSopstatus", filterOptions.visaSop),
        buildCondition("v.ApsStatus", filterOptions.apsStatus),
        buildCondition("v.VisaFileStatus", filterOptions.visaFile),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(v.VisaDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";

      const queries = {
        totalStudents: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode JOIN intakemas i ON i.intakeCode = s.intakeCode JOIN catmas ct ON ct.catCode = s.catCode JOIN locmas lo ON lo.locCode = s.locCode WHERE s.StdCode IS NOT NULL ${whereClause}`,
        visaPackage: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode WHERE s.packageCode IN (2, 3,4,5,10) ${whereClause}`,
        visaProcessStarted: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode WHERE s.packageCode IN (2, 3,4,5,10) AND s.PaymentStatus IN ('Accommodation Amount pending', 'Visa Process Started', 'Completed', 'Visa Amount pending') ${whereClause}`,
        visaProcessBachelor: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode WHERE s.packageCode IN (2, 3,4,5,10) AND s.PaymentStatus IN ('Accommodation Amount pending', 'Visa Process Started', 'Completed', 'Visa Amount pending') AND s.coursetype = 'B' ${whereClause}`,
        visaProcessMaster: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode WHERE s.packageCode IN (2, 3,4,5,10) AND s.PaymentStatus IN ('Accommodation Amount pending', 'Visa Process Started', 'Completed', 'Visa Amount pending') AND s.coursetype = 'M' ${whereClause}`,
      };

      const query = `SELECT s.ststatus, Count(DISTINCT s.StdCode) AS Status, 
          COUNT(DISTINCT CASE WHEN s.packageCode IN (2, 3,4,5,10) THEN s.StdCode END) AS VisaPackage, 
          COUNT(DISTINCT CASE WHEN s.packageCode IN (2, 3,4,5,10) AND s.PaymentStatus IN ('Accommodation Amount pending', 'Visa Process Started', 'Completed', 'Visa Amount pending') THEN s.StdCode END) AS VisaProcessStarted, 
          COUNT(DISTINCT CASE WHEN s.packageCode IN (2, 3,4,5,10) AND s.PaymentStatus IN ('Accommodation Amount pending', 'Visa Process Started', 'Completed', 'Visa Amount pending') AND s.coursetype = 'M' THEN s.StdCode END) AS Master, 
          COUNT(DISTINCT CASE WHEN s.packageCode IN (2, 3,4,5,10) AND s.PaymentStatus IN ('Accommodation Amount pending', 'Visa Process Started', 'Completed', 'Visa Amount pending') AND s.coursetype = 'B' THEN s.StdCode END) AS Bachelor 
        FROM StudentMas s
        LEFT JOIN admissionmas a ON s.StdCode = a.StdCode 
        LEFT JOIN visamasnew v ON s.StdCode = v.StdCode 
        WHERE s.StdCode IS NOT NULL ${whereClause}
        GROUP BY s.ststatus`;

      const result = await fetchBaseQuery(query);
      const responseObj = {
        resultArray: result,
      };

      for (const que in queries) {
        const subResult = await fetchBaseQuery(queries[que]);
        responseObj[que] = {
          count: subResult[0]?.Column1,
          query: queries[que],
        };
      }
      setVisaReportStatusWise(responseObj);
    } catch (error) {
      console.error(error);
      setVisaReportStatusWiseLoading(false);
    } finally {
      setVisaReportStatusWiseLoading(false);
    }
  };

  const fetchVisaReports = async () => {
    setVisaReportStatusLoading(true);
    try {
      const filters = [];
      const conditions = [
        buildCondition("a.intakeCode", filterOptions.appIntake, true),
        buildCondition("a.status", filterOptions.admStatus),
        buildCondition("a.visaSopstatus", filterOptions.visaSop),
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.PaymentStatus", filterOptions.paymentStatus, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("s.ststatus", filterOptions.ststatus, true),
        buildCondition("s.catCode", filterOptions.category, true),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("a.publicorprivate", filterOptions.publicPrivate),
        buildCondition("v.BA_Status", filterOptions.baStatus),
        buildCondition("v.VisaStatus", filterOptions.visaStatus),
        buildCondition("v.VisaFileStatus", filterOptions.visaFile),
        buildCondition("v.ApsStatus", filterOptions.apsStatus),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(v.VisaDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";

      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode JOIN intakemas i ON i.intakeCode = s.intakeCode JOIN catmas ct ON ct.catCode = s.catCode JOIN locmas lo ON lo.locCode = s.locCode";

      const queries = {
        finalAccepted: `${baseQuery} WHERE a.status IN ('Final Admission Accepted')`,
        finalAcceptedPublic: `${baseQuery} WHERE a.status IN ('Final Admission Accepted') AND a.publicorprivate = 'Public'`,
        finalAcceptedPrivate: `${baseQuery} WHERE a.status IN ('Final Admission Accepted') AND a.publicorprivate = 'Private'`,
        finalAcceptedOther: `${baseQuery} WHERE a.status IN ('Final Admission Accepted') AND (a.publicorprivate IS NULL OR a.publicorprivate = '') ${whereClause}`,
        visaSopFinal: `${baseQuery} WHERE a.visaSopstatus IN (107) ${whereClause}`,
        visaSopFinalNull: `${baseQuery} WHERE a.visaSopstatus NOT IN (107) ${whereClause}`,
        visaSopUploaded: `${baseQuery} WHERE a.visaSopstatus IN (107) AND a.visaSop IS NOT NULL ${whereClause}`,
        visaSopUploadedNull: `${baseQuery} WHERE a.visaSopstatus IN (107) AND a.visaSop IS NULL ${whereClause}`,
        apsRecieved: `${baseQuery} WHERE v.ApsStatus IN ('8)APS RECIEVED & UPLOADED ON DROPBOX','8)APS RECEIVED & UPLOADED ON DROPBOX','FOREIGN DEGREE','APS RECEIVED(SELF APPLIED)','NOT APPLICABLE', 'NA') ${whereClause}`,
        apsRecievedNull: `${baseQuery} WHERE v.ApsStatus NOT IN ('8)APS RECIEVED & UPLOADED ON DROPBOX','8)APS RECEIVED & UPLOADED ON DROPBOX','FOREIGN DEGREE','APS RECEIVED(SELF APPLIED)','NOT APPLICABLE', 'NA') ${whereClause}`,
        apsUploaded: `${baseQuery} WHERE v.Apsfilename IS NOT NULL AND v.Apsfilename != '' ${whereClause}`,
        apsNotUploaded: `${baseQuery} WHERE (v.Apsfilename IS NULL OR v.Apsfilename = '') ${whereClause}`,
        passportUploaded: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode WHERE sd.filename IS NOT NULL AND sd.filename != '' AND d.doccode = 81 ${whereClause}`,
        passportNotUploaded: `SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN StdDocMas sd ON s.StdCode = sd.stdcode LEFT JOIN DocMas d ON d.doccode = sd.doccode LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN visamasnew v ON s.StdCode = v.StdCode WHERE (sd.filename IS NULL OR sd.filename = '') AND d.doccode = 81 ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      setVisaReportWise(responseObj);
    } catch (error) {
      console.error(error);
      setVisaReportStatusLoading(false);
    } finally {
      setVisaReportStatusLoading(false);
    }
  };

  const fetchVisaBaReport = async () => {
    setBaLoading(true);
    try {
      const filters = [];
      const conditions = [
        buildCondition("a.intakeCode", filterOptions.appIntake, true),
        buildCondition("a.status", filterOptions.admStatus),
        buildCondition("a.visaSopstatus", filterOptions.visaSop),
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.PaymentStatus", filterOptions.paymentStatus, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("s.ststatus", filterOptions.ststatus, true),
        buildCondition("s.catCode", filterOptions.category, true),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("a.status", filterOptions.admStatus),
        buildCondition("a.publicorprivate", filterOptions.publicPrivate),
        buildCondition("v.BA_Status", filterOptions.baStatus),
        buildCondition("v.VisaStatus", filterOptions.visaStatus),
        buildCondition("v.VisaFileStatus", filterOptions.visaFile),
        buildCondition("v.ApsStatus", filterOptions.apsStatus),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(v.VisaDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode) FROM StudentMas s LEFT JOIN visamasnew v ON s.StdCode = v.StdCode";
      const queries = {
        baNotStarted: `${baseQuery} WHERE v.BA_Status IN ('0) BA NOT STARTED DUE TO LOW PAYMENT', '0) BA Process not started due to low payment') ${whereClause}`,
        baIntroDone: `${baseQuery} WHERE v.BA_Status IN ('1) BA Intro Email done & Checklist sent by ERP', '1)BA INTRO EMAIL AND CALL', '2) BA Intro Call Done', 'BA INTRO CALL DONE') ${whereClause}`,
        baOpened: `${baseQuery} WHERE v.BA_Status IN ('2)BA OPENED', '3) BA Opened') ${whereClause}`,
        fundTransferred: `${baseQuery} WHERE v.BA_Status IN ('3)FUNDS TRANSFERRED', '3)FUNDS TRANSFERRED') ${whereClause}`,
        notUpdate: `${baseQuery} WHERE (v.BA_Status IS NULL OR v.BA_Status = '') ${whereClause}`,
        notApplicable: `${baseQuery} WHERE v.BA_Status IN ('BLOCKED', 'N/A', 'Not Applicable')  ${whereClause}`,
        visaProcessNotStarted: `${baseQuery} WHERE v.VisaStatus IN ('0) Visa Process not started due to low payment', '0)VISA NOT STARTED DUE TO LOW PAYMENT')  ${whereClause}`,
        checklistSent: `${baseQuery} WHERE v.VisaStatus IN ('1) VISA CHECKLIST SENT BY ERP', '1) Visa Process Email done & Checklist sent by ER', '1) Visa Process Email done & Checklist sent by ERP')  ${whereClause}`,
        callDone: `${baseQuery} WHERE v.VisaStatus IN ('2) Visa Process Call Done/Google Meet', '2) VISA PROCESS EMAIL & CALL DONE', 'VISA PROCESS CALL DONE')  ${whereClause}`,
        docPrepared: `${baseQuery} WHERE v.VisaStatus IN ('3) YG VISA DOCS PREPARED')  ${whereClause}`,
        docSent: `${baseQuery} WHERE v.VisaStatus IN ('4) YG VISA DOCS VERIFIED AND SENT', '7) Full file recieved & Verified by YG')  ${whereClause}`,
        mockOneDone: `${baseQuery} WHERE v.VisaStatus IN ('6) Mock One Done')  ${whereClause}`,
        mockTwoDone: `${baseQuery} WHERE v.VisaStatus IN ('7) Mock Two Done', '8) MOCK TWO DONE')  ${whereClause}`,
        finRecieved: `${baseQuery} WHERE v.VisaStatus IN ('5) FINANCIALS RECEIVED')  ${whereClause}`,
        notApplicableVisa: `${baseQuery} WHERE v.VisaStatus IN ('N.A.', 'Not Applicable')  ${whereClause}`,
        notUpdatedVisa: `${baseQuery} WHERE (v.VisaStatus IS NULL OR v.VisaStatus = '')  ${whereClause}`,
        applied: `${baseQuery} WHERE v.VisaFileStatus IN ('2) APPLIED', '7) Applied')  ${whereClause}`,
        reApplied: `${baseQuery} WHERE v.VisaFileStatus IN ('11) To be re-applied')  ${whereClause}`,
        toFilled: `${baseQuery} WHERE v.VisaFileStatus IN ('0) To be Filled')  ${whereClause}`,
        partiallyFilled: `${baseQuery} WHERE v.VisaFileStatus IN ('1) Partially Filled')  ${whereClause}`,
        underReview: `${baseQuery} WHERE v.VisaFileStatus IN ('2) Under Review')  ${whereClause}`,
        appointmentNotBooked: `${baseQuery} WHERE v.VisaFileStatus IN ('0) APPOINTMENT NOT BOOKED')  ${whereClause}`,
        toLaunch: `${baseQuery} WHERE v.VisaFileStatus IN ('3) Ready to Launch')  ${whereClause}`,
        waitlisted: `${baseQuery} WHERE v.VisaFileStatus IN ('4) Launched & Waitlisted', 'WAITLISTED')  ${whereClause}`,
        stamped: `${baseQuery} WHERE v.VisaFileStatus IN ('4) STAMPED', '9) Stamped')  ${whereClause}`,
        submitted: `${baseQuery} WHERE v.VisaFileStatus IN ('5) Launched & Submitted')  ${whereClause}`,
        appointmentBooked: `${baseQuery} WHERE v.VisaFileStatus IN ('6) Appointment Booked')  ${whereClause}`,
        rejected: `${baseQuery} WHERE v.VisaFileStatus IN ('5) REJECTED', '10) Rejected')  ${whereClause}`,
        pendingFileStatus: `${baseQuery} WHERE v.VisaFileStatus IN ('8) TO BE REBOOKED', 'NOT SENT') AND (v.VisaFileStatus IS NULL OR v.VisaFileStatus = '') ${whereClause}`,
      };
      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      setBaReport(responseObj);
    } catch (error) {
      console.error(error);
      setBaLoading(false);
    } finally {
      setBaLoading(false);
    }
  };

  const fetchVisaUniReport = async () => {
    setUniWiseLoading(true);
    try {
      const filters = [];
      const conditions = [
        buildCondition("a.intakeCode", filterOptions.appIntake, true),
        buildCondition("a.status", filterOptions.admStatus),
        buildCondition("a.visaSopstatus", filterOptions.visaSop),
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.PaymentStatus", filterOptions.paymentStatus, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("s.ststatus", filterOptions.ststatus, true),
        buildCondition("s.catCode", filterOptions.category, true),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("a.status", filterOptions.admStatus),
        buildCondition("a.publicorprivate", filterOptions.publicPrivate),
        buildCondition("v.BA_Status", filterOptions.baStatus),
        buildCondition("v.VisaStatus", filterOptions.visaStatus),
        buildCondition("v.VisaFileStatus", filterOptions.visaFile),
        buildCondition("v.ApsStatus", filterOptions.apsStatus),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(v.VisaDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      const query = `SELECT s.Stdid, v.VisaDate, a.status, a.universityName, a.CourseName FROM StudentMas s LEFT JOIN visamasnew v ON s.StdCode = v.StdCode LEFT JOIN admissionmas a ON s.StdCode = a.StdCode WHERE v.VisaDate IS NOT NULL AND a.status = 'Final Admission Accepted' ${whereClause} ORDER BY v.VisaDate ASC`;
      const result = await fetchBaseQuery(query);
      setUniWiseReport(result);
    } catch (error) {
      console.error(error);
      setUniWiseLoading(false);
    } finally {
      setUniWiseLoading(false);
    }
  };

  const handleFilterChange = () => {
    fetchVisaReports();
    fetchVisaBaReport();
    fetchVisaUniReport();
    fetchStatusWiseVisaReports();
  };

  const checkAuthentication = async () => {
    try {
      if (userCode === 0) {
        setAuthState(true);
        return true;
      }

      const checkAdmin = await fetchBaseQuery(
        `SELECT * FROM usermas WHERE UserCode = ${userCode} AND deptcode IN (15, 17, 16, 12, 2)`
      );

      if (Array.isArray(checkAdmin) && checkAdmin.length > 0) {
        setAuthState(true);
        return true;
      }

      setAuthState(false);
      return false;
    } catch (error) {
      console.error("Error checking authentication:", error);
      setAuthState(false);
      return false;
    }
  };
  useEffect(() => {
    checkAuthentication();
  }, [userCode]);

  useEffect(() => {
    fetchFilterDropdown();
    fetchStatusWiseVisaReports();
    fetchVisaReports();
    fetchVisaBaReport();
    fetchVisaUniReport();
  }, []);
  return authState ?
   (
    <div className="transback h-100 overflow-auto p-3 d-flex flex-column">
      <button
        className="btn text-secondary rounded-pill px-3 py-1 d-flex d-lg-none align-items-center gap-2"
        onClick={() => setShowSideOverlay(true)}
      >
        <MenuIcon />
      </button>
      <div className="headerUserDetails py-1 mb-3 d-flex justify-content-between">
        <span>Visa Dashboard</span>
        <div
          className="d-flex gap-2 align-items-center"
          title={`Name: ${user?.name} | Email: ${user?.email}`}
        >
          <i className="fa fa-user"></i>
          <span>{user?.name}</span>
        </div>
      </div>

      <div className="w-100 h-100 overflow-auto ">
        {spinnerLoading ? (
          <div className="w-100 h-100 d-flex align-items-center justify-content-center">
            <CustomSpinner size={1} />
          </div>
        ) : (
          <>
            <div className="container-fluid">
              <div className="header py-3 border border-light px-2 rounded bg-light">
                <h5>Filter</h5>
                <div className="row g-2 g-lg-3 mb-3">
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="intake-select"
                        options={filterDropdown.intakeOptions}
                        isMulti
                        isClearable
                        placeholder="Select Intake"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            intake: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="appIntake-select"
                        options={filterDropdown.intakeOptions}
                        isMulti
                        isClearable
                        placeholder="Select Application Intake"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            appIntake: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="package-select"
                        options={filterDropdown.locationOptions}
                        isMulti
                        isClearable
                        placeholder="Select Branch"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            location: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="ststatus-select"
                        options={stStatusOptions}
                        isMulti
                        isClearable
                        placeholder="Select Student Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            ststatus: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="ststatus-select"
                        options={paymentStatusOptions}
                        isMulti
                        isClearable
                        placeholder="Select Payment Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            paymentStatus: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="category-select"
                        options={filterDropdown.categoryOptions}
                        isMulti
                        isClearable
                        placeholder="Select Student Category"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            category: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="package-select"
                        options={packageCodes}
                        isMulti
                        isClearable
                        placeholder="Select Package"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            package: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="baStatus-select"
                        options={baStatusList}
                        isMulti
                        isClearable
                        placeholder="Select BA Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            baStatus: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="visaSop-select"
                        options={filterDropdown.visaSopOptions}
                        isMulti
                        isClearable
                        placeholder="Select Visa SOP Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            visaSop: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="aps-select"
                        options={apsStatusOptions}
                        isMulti
                        isClearable
                        placeholder="Select APS Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            apsStatus: selectedOptions
                              ? selectedOptions.map((opt) =>
                                  parseInt(opt.value)
                                )
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="visaFile-select"
                        options={visaFileStatusOptions}
                        isMulti
                        isClearable
                        placeholder="Select Visa File Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            visaFile: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="status-select"
                        options={admStatusList}
                        isMulti
                        isClearable
                        placeholder="Select Admission Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            admStatus: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="visaStatus-select"
                        options={visaStatusList}
                        isMulti
                        isClearable
                        placeholder="Select Visa Status"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            visaStatus: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="course-select"
                        options={[
                          { label: "Master's", value: "M" },
                          { label: "Bachelor's", value: "B" },
                        ]}
                        isMulti
                        isClearable
                        placeholder="Select Course"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            course: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <Select
                        id="publicPrivate-select"
                        options={[
                          { label: "Public", value: "Public" },
                          { label: "Private", value: "Private" },
                        ]}
                        isMulti
                        isClearable
                        placeholder="Select Public/Private"
                        onChange={(selectedOptions) => {
                          setFilterOptions((prev) => ({
                            ...prev,
                            publicPrivate: selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [],
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-group col-md-2">
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={handleFilterChange}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>
              <div className="w-100">
                <VisaTable
                  visaReportStatusWise={visaReportStatusWise}
                  visaReportStatusWiseLoading={visaReportStatusWiseLoading}
                  visaReportWise={visaReportWise}
                  visaReportStatusLoading={visaReportStatusLoading}
                  baReport={baReport}
                  baLoading={baLoading}
                  uniWiseport={uniWiseport}
                  uniWiseLoading={uniWiseLoading}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ) : (
    <NotAuthorize />
  );
};

export default VisaDashboard;
